import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useState } from "react";
import { toast } from "sonner";

import * as pickersApi from "@/api/one_c_pickers";
import * as settingsApi from "@/api/system_settings";
import { PickerField } from "@/components/pickers/PickerField";
import { savedPickerValue, type PickerRow } from "@/components/pickers/RemotePicker";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toastError } from "@/lib/api-errors";
import {
  searchDepartments,
  searchOrganizations,
  searchWarehouses,
} from "@/pages/settings/cards/one-c-search";
import { usePortalConfig } from "@/portal/usePortalConfig";

/** Пара «ссылка + имя» для каждой настройки ФБС. Имя хранится рядом со
 *  ссылкой, чтобы страница открывалась без обращения к 1С. */
interface OneCForm {
  fbs_owner_org_ref: string;
  fbs_owner_org_name: string;
  fbs_owner_kontragent_ref: string;
  fbs_owner_kontragent_name: string;
  fbs_source_wh_ref: string;
  fbs_source_wh_name: string;
  fbs_department_ref: string;
  fbs_department_name: string;
}

const EMPTY_ONE_C: OneCForm = {
  fbs_owner_org_ref: "",
  fbs_owner_org_name: "",
  fbs_owner_kontragent_ref: "",
  fbs_owner_kontragent_name: "",
  fbs_source_wh_ref: "",
  fbs_source_wh_name: "",
  fbs_department_ref: "",
  fbs_department_name: "",
};

/** Только для предупреждения «не заполнено» — по ссылке, а не по имени:
 *  настройка рабочая, даже если имя не подтянулось. */
const ONE_C_REQUIRED: [keyof OneCForm, string][] = [
  ["fbs_owner_org_ref", "организация-владелец"],
  ["fbs_source_wh_ref", "склад-источник добора"],
  ["fbs_department_ref", "подразделение"],
];

/** Реквизиты, которые нужны ТОЛЬКО для ПТМО. Где организация одна, документа
 *  нет — и просить их значит просить заполнить реквизиты того, чего не
 *  будет. */
const ONE_C_REQUIRED_PTMO: [keyof OneCForm, string][] = [
  ["fbs_owner_kontragent_ref", "контрагент-владелец"],
];

export function FbsOneCRefsCard(): React.ReactElement {
  const qc = useQueryClient();
  const { ptmo } = usePortalConfig();
  const { data } = useQuery({ queryKey: ["system-settings"], queryFn: settingsApi.getSystemSettings });
  const [form, setForm] = useState<OneCForm>(EMPTY_ONE_C);

  useEffect(() => {
    if (!data) return;
    setForm({
      fbs_owner_org_ref: data.fbs_owner_org_ref ?? "",
      fbs_owner_org_name: data.fbs_owner_org_name ?? "",
      fbs_owner_kontragent_ref: data.fbs_owner_kontragent_ref ?? "",
      fbs_owner_kontragent_name: data.fbs_owner_kontragent_name ?? "",
      fbs_source_wh_ref: data.fbs_source_wh_ref ?? "",
      fbs_source_wh_name: data.fbs_source_wh_name ?? "",
      fbs_department_ref: data.fbs_department_ref ?? "",
      fbs_department_name: data.fbs_department_name ?? "",
    });
  }, [data]);

  const saveMut = useMutation({
    // Отправляем ровно то, что в форме: незатронутые поля пришли с сервера и
    // уходят обратно без изменений — сохранение не может «сбросить» боевую
    // ссылку только потому, что пикер не ходил в 1С.
    mutationFn: () => settingsApi.updateSystemSettings(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["system-settings"] });
      toast.success("Сохранено");
    },
    onError: toastError("Ошибка сохранения"),
  });

  const set = (patch: Partial<OneCForm>) => setForm((s) => ({ ...s, ...patch }));
  const pick = (refKey: keyof OneCForm, nameKey: keyof OneCForm) => (r: PickerRow | null) =>
    set({ [refKey]: r?.ref ?? "", [nameKey]: r?.name ?? "" } as Partial<OneCForm>);

  const required = ptmo ? [...ONE_C_REQUIRED, ...ONE_C_REQUIRED_PTMO] : ONE_C_REQUIRED;
  const missing = required.filter(([key]) => !form[key].trim());

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Ссылки 1С</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {missing.length > 0 && (
          <p className="rounded bg-amber-50 px-2 py-1 text-xs text-amber-800">
            Не заполнено: {missing.map(([, label]) => label).join(", ")}. Пока эти поля
            пустые, завершить добор не получится.
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <PickerField
            label="Организация-владелец товара"
            hint="отправитель ПТМО"
            placeholder="Начните вводить название организации…"
            value={savedPickerValue(form.fbs_owner_org_ref, form.fbs_owner_org_name)}
            search={searchOrganizations}
            queryKey={["one-c-organizations"]}
            onChange={pick("fbs_owner_org_ref", "fbs_owner_org_name")}
          />
          {ptmo && (
          <PickerField
            label="Контрагент-владелец"
            hint="то же юрлицо, но как контрагент"
            placeholder="Начните вводить название контрагента…"
            value={savedPickerValue(
              form.fbs_owner_kontragent_ref,
              form.fbs_owner_kontragent_name,
            )}
            search={(q) => pickersApi.searchKontragents(q)}
            queryKey={["one-c-kontragents"]}
            onChange={pick("fbs_owner_kontragent_ref", "fbs_owner_kontragent_name")}
          />
          )}
          {/* ⚠️ «Склада ФБС» здесь БОЛЬШЕ НЕТ: комната живёт у СКЛАДА ПРОДАВЦА
              (карточка кабинета → строка склада). Общая настройка была вторым
              источником того же значения и подменяла незаполненную строку — у
              инсталляции с двумя складами это свозило обе комнаты в одну, а
              там, где строку заполнили, правка настройки просто переставала
              действовать. */}
          <PickerField
            label="Склад-источник завоза"
            hint="по умолчанию — Склад МП"
            placeholder="Начните вводить название склада…"
            value={savedPickerValue(form.fbs_source_wh_ref, form.fbs_source_wh_name)}
            search={searchWarehouses}
            queryKey={["one-c-warehouses"]}
            onChange={pick("fbs_source_wh_ref", "fbs_source_wh_name")}
          />
          <PickerField
            label="Подразделение"
            hint="подставляется в документы"
            placeholder="Начните вводить название подразделения…"
            value={savedPickerValue(form.fbs_department_ref, form.fbs_department_name)}
            search={searchDepartments}
            queryKey={["one-c-departments"]}
            onChange={pick("fbs_department_ref", "fbs_department_name")}
          />
        </div>
        <Button size="sm" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
          Сохранить
        </Button>
      </CardContent>
    </Card>
  );
}
